/**
 * Panel ustawień SMSAPI
 * Konfiguracja integracji z smsapi.pl
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const SmsApiSettings = () => {
  const [settings, setSettings] = useState({
    api_token: '',
    sender_name: 'V3SYS',
    enabled: false,
    shop_name: 'Sklep',
    template_order_ready: 'Twoje zamówienie #{order_id} jest gotowe do odbioru. {shop_name}',
    template_order_shipped: 'Twoje zamówienie #{order_id} zostało wysłane. Numer śledzenia: {tracking}. {shop_name}',
    template_order_confirmed: 'Dziękujemy za zamówienie #{order_id}. Potwierdzamy przyjęcie do realizacji. {shop_name}'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [balance, setBalance] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Test SMS z systemu V3SYS');
  const [sendingTest, setSendingTest] = useState(false);

  // Pobierz ustawienia
  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/sms/settings');
      const data = response.data;
      if (data.success) {
        setSettings(prev => ({ ...prev, ...data.data }));
      }
    } catch (error) {
      console.error('Błąd pobierania ustawień SMS:', error);
      setMessage({ text: 'Błąd pobierania ustawień', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  // Pobierz stan konta
  const fetchBalance = useCallback(async () => {
    try {
      const response = await api.get('/sms/balance');
      const data = response.data;
      if (data.success) {
        setBalance(data.data);
      }
    } catch (error) {
      console.error('Błąd pobierania stanu konta:', error);
    }
  }, []);

  // Pobierz logi
  const fetchLogs = useCallback(async () => {
    try {
      const response = await api.get('/sms/logs?limit=50');
      const data = response.data;
      if (data.success) {
        setLogs(data.data || []);
      }
    } catch (error) {
      console.error('Błąd pobierania logów SMS:', error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (settings.has_token) {
      fetchBalance();
    }
  }, [settings.has_token, fetchBalance]);

  // Zapisz ustawienia
  const handleSave = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await api.post('/sms/settings', settings);
      const data = response.data;
      
      if (data.success) {
        setMessage({ text: 'Ustawienia zapisane pomyślnie', type: 'success' });
        fetchBalance();
      } else {
        setMessage({ text: data.message || 'Błąd zapisu', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Błąd połączenia z serwerem', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Wyślij testowy SMS
  const handleSendTest = async () => {
    if (!testPhone) {
      setMessage({ text: 'Podaj numer telefonu', type: 'error' });
      return;
    }

    setSendingTest(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await api.post('/sms/test', { phone: testPhone, message: testMessage });
      const data = response.data;
      
      if (data.success) {
        setMessage({ text: 'SMS testowy wysłany!', type: 'success' });
        fetchLogs();
      } else {
        setMessage({ text: data.error || data.message || 'Błąd wysyłki', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Błąd połączenia z serwerem', type: 'error' });
    } finally {
      setSendingTest(false);
    }
  };

  // Obsługa zmiany pól
  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Ładowanie...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', width: '100%', margin: '0 auto', padding: '20px', boxSizing: 'border-box' }}>
      <h2 style={{ marginBottom: '20px' }}>
        📱 Integracja SMSAPI.pl
      </h2>

      {/* Komunikaty */}
      {message.text && (
        <div 
          className={`alert alert-${message.type === 'error' ? 'danger' : 'success'}`}
          style={{ marginBottom: '20px' }}
        >
          {message.text}
        </div>
      )}

      {/* Stan konta */}
      {balance && (
        <div className="card mb-4" style={{ backgroundColor: 'var(--success-light)', border: '1px solid var(--success)' }}>
          <div className="card-body">
            <h5 className="card-title">💰 Stan konta SMSAPI</h5>
            <p className="mb-1">
              <strong>Punkty SMS:</strong> {balance.points?.toFixed(2) || 'N/A'}
            </p>
            {balance.name && <p className="mb-0"><strong>Konto:</strong> {balance.name}</p>}
          </div>
        </div>
      )}

      {/* Podstawowe ustawienia */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">⚙️ Konfiguracja</h5>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Token API SMSAPI</label>
            <input
              type="password"
              className="form-control"
              value={settings.api_token}
              onChange={(e) => handleChange('api_token', e.target.value)}
              placeholder="Wprowadź token z panelu SMSAPI..."
            />
            <small className="text-muted">
              Token znajdziesz w panelu SMSAPI → Ustawienia → API
            </small>
          </div>

          <div className="mb-3">
            <label className="form-label">Nazwa nadawcy (Sender ID)</label>
            <input
              type="text"
              className="form-control"
              value={settings.sender_name}
              onChange={(e) => handleChange('sender_name', e.target.value)}
              maxLength={11}
              placeholder="V3SYS"
            />
            <small className="text-muted">
              Max 11 znaków. Musi być zarejestrowany w SMSAPI.
            </small>
          </div>

          <div className="mb-3">
            <label className="form-label">Nazwa sklepu (używana w szablonach)</label>
            <input
              type="text"
              className="form-control"
              value={settings.shop_name}
              onChange={(e) => handleChange('shop_name', e.target.value)}
              placeholder="Nazwa sklepu"
            />
          </div>

          <div className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="smsEnabled"
              checked={settings.enabled}
              onChange={(e) => handleChange('enabled', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="smsEnabled">
              <strong>Włącz wysyłanie SMS</strong>
            </label>
          </div>
        </div>
      </div>

      {/* Szablony SMS */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">📝 Szablony wiadomości SMS</h5>
        </div>
        <div className="card-body">
          <p className="text-muted mb-3">
            Dostępne zmienne: <code>{'{order_id}'}</code>, <code>{'{tracking}'}</code>, 
            <code>{'{shop_name}'}</code>, <code>{'{customer_name}'}</code>
          </p>

          <div className="mb-3">
            <label className="form-label">
              📦 Zamówienie gotowe do odbioru
            </label>
            <textarea
              className="form-control"
              rows={2}
              value={settings.template_order_ready}
              onChange={(e) => handleChange('template_order_ready', e.target.value)}
              placeholder="Twoje zamówienie #{order_id} jest gotowe..."
            />
            <small className="text-muted">
              Wysyłane przy statusie: "ready", "gotowe"
            </small>
          </div>

          <div className="mb-3">
            <label className="form-label">
              🚚 Zamówienie wysłane
            </label>
            <textarea
              className="form-control"
              rows={2}
              value={settings.template_order_shipped}
              onChange={(e) => handleChange('template_order_shipped', e.target.value)}
              placeholder="Twoje zamówienie #{order_id} zostało wysłane..."
            />
            <small className="text-muted">
              Wysyłane przy statusie: "shipped", "wyslane"
            </small>
          </div>

          <div className="mb-3">
            <label className="form-label">
              ✅ Zamówienie potwierdzone
            </label>
            <textarea
              className="form-control"
              rows={2}
              value={settings.template_order_confirmed}
              onChange={(e) => handleChange('template_order_confirmed', e.target.value)}
              placeholder="Dziękujemy za zamówienie #{order_id}..."
            />
            <small className="text-muted">
              Wysyłane przy statusie: "confirmed", "potwierdzone"
            </small>
          </div>
        </div>
      </div>

      {/* Przycisk zapisu */}
      <div className="d-grid gap-2 mb-4">
        <button
          className="btn btn-primary btn-lg"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '⏳ Zapisywanie...' : '💾 Zapisz ustawienia'}
        </button>
      </div>

      {/* Testowa wysyłka */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">🧪 Test wysyłki SMS</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">Numer telefonu</label>
              <input
                type="tel"
                className="form-control"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+48 123 456 789"
              />
            </div>
            <div className="col-md-8 mb-3">
              <label className="form-label">Treść wiadomości</label>
              <input
                type="text"
                className="form-control"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="Wiadomość testowa..."
              />
            </div>
          </div>
          <button
            className="btn btn-secondary"
            onClick={handleSendTest}
            disabled={sendingTest || !settings.enabled}
          >
            {sendingTest ? '⏳ Wysyłanie...' : '📤 Wyślij testowy SMS'}
          </button>
          {!settings.enabled && (
            <small className="text-muted ms-3">
              Włącz najpierw wysyłanie SMS
            </small>
          )}
        </div>
      </div>

      {/* Historia SMS */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">📋 Historia wysłanych SMS</h5>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              setShowLogs(!showLogs);
              if (!showLogs) fetchLogs();
            }}
          >
            {showLogs ? 'Ukryj' : 'Pokaż'}
          </button>
        </div>
        {showLogs && (
          <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {logs.length === 0 ? (
              <p className="text-muted text-center">Brak wysłanych wiadomości</p>
            ) : (
              <div className="admin-table-wrap">
              <table className="table table-sm table-striped">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Telefon</th>
                    <th>Wiadomość</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={idx}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.85em' }}>
                        {new Date(log.created_at).toLocaleString('pl-PL')}
                      </td>
                      <td style={{ fontSize: '0.9em' }}>{log.phone}</td>
                      <td style={{ fontSize: '0.85em', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.message}
                      </td>
                      <td>
                        <span className={`badge ${log.status === 'sent' ? 'bg-success' : 'bg-danger'}`}>
                          {log.status === 'sent' ? '✅' : '❌'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Link do dokumentacji */}
      <div className="mt-4 text-center">
        <a 
          href="https://www.smsapi.pl/docs/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-muted"
        >
          📚 Dokumentacja SMSAPI.pl
        </a>
      </div>
    </div>
  );
};

export default SmsApiSettings;
